import { trackAnalytics, type AppConfiguration } from '@puffin/app-frame-api';
import { Alert, Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormLabel } from '@mui/material';
import { useState } from 'react';
import { openBrowserNotificationPermissionDialog, useBrowserNotificationPermission } from '../../state/notifications/browser-notification-permission';
import { ConfigurationFields } from './ConfigurationFields';
import { saveAppearancePreference, useAppearancePreference } from '../../state/configuration/appearance-preference';

const appearanceOptions = [
  { value: 'auto', label: 'Auto' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
] as const;

interface ApplicationSettingsDialogProps {
  configuration: AppConfiguration;
  onClose: (configuration: AppConfiguration) => void;
}

export const ApplicationSettingsDialog = ({ configuration, onClose }: ApplicationSettingsDialogProps) => {
  const [team, setTeam] = useState(configuration.team);
  const [role, setRole] = useState(configuration.role);
  const notificationPermission = useBrowserNotificationPermission();
  const appearance = useAppearancePreference();

  return (
    <Dialog open fullWidth maxWidth="sm" aria-labelledby="application-settings-title">
      <DialogTitle id="application-settings-title">Settings</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, pt: 1 }}>
          <ConfigurationFields team={team} role={role} onTeamChange={setTeam} onRoleChange={setRole} />
          {notificationPermission === 'unsupported' ? (
            <Alert severity="info">Browser notifications are unavailable here. Use a supported browser over HTTPS.</Alert>
          ) : notificationPermission !== 'granted' ? (
            <Button onClick={() => openBrowserNotificationPermissionDialog('Get notified when a long-running task finishes.')}>
              {notificationPermission === 'denied' ? 'Review browser notification settings' : 'Enable browser notifications'}
            </Button>
          ) : null}
          <FormControl fullWidth>
            <FormLabel id="appearance-label" sx={{ mb: 1 }}>
              Appearance
            </FormLabel>
            <ButtonGroup aria-labelledby="appearance-label" fullWidth>
              {appearanceOptions.map(({ value, label }) => (
                <Button
                  key={value}
                  variant={appearance === value ? 'contained' : 'outlined'}
                  aria-pressed={appearance === value}
                  onClick={() => saveAppearancePreference(value)}
                >
                  {label}
                </Button>
              ))}
            </ButtonGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            if (team !== configuration.team || role !== configuration.role) {
              trackAnalytics('APPLICATION_CONFIGURATION_SAVED', { team, role, configurationUpdated: true });
            }
            onClose({ team, role });
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
